import { useMemo } from 'react';
import { useGetCurrentUserQuery, useGetStockingPointsQuery } from '@/graphql/generated/hooks';

/**
 * The workshops the signed-in user may put someone on (ADR-0025, ADR-0026).
 *
 * Bounded the way the role picker is: someone who already belongs to workshops
 * may only staff those, while an administrator belongs to none and may staff
 * any. The server enforces the same bound, so offering more would be a menu of
 * errors.
 */
export function useStaffableWorkshops() {
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: workshopData } = useGetStockingPointsQuery({
    variables: { input: { types: ['WORKSHOP'], isActive: true } },
  });

  return useMemo(() => {
    const ownIds = new Set(
      (currentUser?.me?.stockingPointMemberships ?? [])
        .map(membership => membership.stockingPoint?.id)
        .filter((id): id is string => Boolean(id))
    );

    const workshops = (workshopData?.stockingPoints?.items ?? []).filter(
      workshop => ownIds.size === 0 || ownIds.has(workshop.id ?? '')
    );

    return {
      workshops,
      options: workshops.map(workshop => ({
        value: workshop.id ?? '',
        label: workshop.name ?? '',
      })),
    };
  }, [currentUser, workshopData]);
}
